import { useContext } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router";

import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { redirectToLoginPage } from "utilities/apiHandler";
import { usePreviousLocation } from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation";
import { routes } from "utilities/routes";

import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";

import InternalUser from "entity/internalUser/InternalUserDetails/InternalUser";
import { InternalUserTabKeys } from "entity/internalUser/InternalUserDetails/InternalUser.api.types";

/**
 * Resolves the requested tab from the URL, defaulting to (and falling back to) the
 * Overview tab when the `initialTab` param is missing or not a known tab key.
 */
export function resolveInitialTab(search: string): InternalUserTabKeys {
    const raw = new URLSearchParams(search).get("initialTab");
    const isKnownTab = (Object.values(InternalUserTabKeys) as string[]).includes(raw ?? "");
    return isKnownTab ? (raw as InternalUserTabKeys) : InternalUserTabKeys.Overview;
}

export interface IRouteMyUserDetailsProps {
    /**
     * `"modal"` — wraps InternalUser in a BTModal (used by RouteRelative in MainNavigation
     * for in-app overlay navigation, e.g. the avatar "My Preferences" dropdown).
     *
     * `"page"` — renders InternalUser directly as a page component without modal wrapping
     * (used by FullPageLayout for direct-route entry points such as email deep links).
     */
    displayMode?: "modal" | "page";
}

/**
 * Id-less entry point for a user's OWN Internal User Details.
 *
 * Distributed as `/app/MyUserDetails?initialTab=<tab>` (no user id embedded) so the same
 * link works in email blasts, and used by the avatar "My Preferences" dropdown. Resolves
 * the current session user's globalUserId from UserInfoContext and renders the details
 * component — the id lives in memory and never appears in the URL.
 *
 * Two rendering modes:
 * - **modal** (default): wraps InternalUser in a BTModal, used for in-app overlay
 *   navigation via RouteRelative in MainNavigation. On close, returns the user to the
 *   page they came from (or the portal landing page for cold visits).
 * - **page**: renders InternalUser as a standalone page component inside FullPageLayout,
 *   used for direct-route entry points (email deep links). The FullPageLayout provides
 *   the nav bar and SPA chrome.
 *
 * Logged-out cold visits never reach a resolvable user here: the top-level AppProvider's
 * /api/AccountInfo/GlobalInfo call 401s first and the global interceptor redirects to
 * login with `lpr` set to this route, returning the user here after a successful login.
 *
 * A missing globalUserId despite a resolved userInfo means the session lapsed at the
 * user level even though the builder-level context still resolved (see
 * AccountInfoManager.GetUserInfoAsync) — the same "not really logged in" case the 401
 * handler covers. Route it through the identical redirectToLoginPage flow so `lpr` sends
 * the user back here after re-auth instead of stranding them on their portal landing page.
 */
const RouteMyUserDetailsInternal = ({ displayMode = "modal" }: IRouteMyUserDetailsProps) => {
    const userInfo = useContext(UserInfoContext);
    const location = useLocation();
    const history = useHistory();
    const match = useRouteMatch();
    const previous = usePreviousLocation();

    const globalUserId = userInfo?.globalUserId ?? null;
    if (globalUserId == null) {
        redirectToLoginPage(false);
        return <BTLoading />;
    }

    const landing = routes.landingPage.getLinkForLoginType(userInfo!.loginType, false);
    const initialTab = resolveInitialTab(location.search);

    return (
        <InternalUser
            id={globalUserId}
            initialTab={initialTab}
            modalConfig={
                displayMode === "modal"
                    ? {
                          parentRoute: landing,
                          beforeClose: () => history.replace(previous?.pathname ?? landing),
                      }
                    : undefined
            }
            history={history}
            location={location}
            match={match}
        />
    );
};

export const RouteMyUserDetails = withErrorBoundary(RouteMyUserDetailsInternal)(
    "Could not load your user details",
);

export default RouteMyUserDetails;
